import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Rocket, Check } from "lucide-react";
import api from "../lib/api";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "baru saja";
  if (s < 3600) return `${Math.floor(s / 60)} mnt lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  return `${Math.floor(s / 86400)} hari lalu`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read", {}); } catch { /* ignore */ }
      setUnread(0);
      setItems((s) => s.map((n) => ({ ...n, read: true })));
    }
  };

  const openItem = (n) => {
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={toggle}
        className="relative w-10 h-10 rounded-full grid place-items-center border-2 border-su-border bg-su-surface hover:border-su-primary transition-colors"
        title="Notifikasi"
      >
        <Bell className="w-5 h-5 text-su-primary" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-su-danger text-white text-[10px] font-heading font-black">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-w-[90vw] card-chunky p-0 overflow-hidden z-50">
          <div className="px-4 py-3 border-b-2 border-su-border flex items-center justify-between">
            <span className="font-heading font-black text-sm">Notifikasi</span>
            <Bell className="w-4 h-4 text-su-muted" />
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-su-muted font-medium text-sm">Belum ada notifikasi.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-su-border/60 hover:bg-su-primary/5 transition-colors flex gap-3 ${n.read ? "opacity-70" : ""}`}
                >
                  <span className="w-9 h-9 rounded-xl bg-su-danger/15 grid place-items-center shrink-0">
                    <Rocket className="w-4 h-4 text-su-danger" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="font-heading font-black text-sm block flex items-center gap-1">
                      {n.title}{!n.read && <span className="w-2 h-2 rounded-full bg-su-primary inline-block" />}
                    </span>
                    <span className="text-xs text-su-muted font-medium block">{n.body}</span>
                    <span className="text-[10px] text-su-muted font-bold uppercase mt-1 block">{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
